import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "./App.css";
import apiClient from "./services/apiClient";

import {
  FiMenu,
  FiHome,
  FiUsers,
  FiArchive,
  FiBarChart2,
  FiPlusSquare,
  FiSettings,
  FiLogOut,
  FiChevronDown,
  FiSend,
  FiBook,
  FiZap,
} from "react-icons/fi";

import logoFull from "./assets/logo.png";
import logoMark from "./assets/logo.png";

const SharedLayout = ({ children, pageTitle }) => {
  const { userId } = useParams();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [activeMenu, setActiveMenu] = useState("ANASAYFA");
  const [contentMenuOpen, setContentMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const toggleSidebar = () => setSidebarCollapsed((s) => !s);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) setSidebarCollapsed(true);
    };
    handleResize(); // ilk yüklemede
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (location.pathname === `/${userId}/dashboard`) setActiveMenu("ANASAYFA");
    else if (location.pathname === `/${userId}/create-course`) {
      setActiveMenu("İÇERİK OLUŞTUR");
      setContentMenuOpen(true);
    } else if (location.pathname === `/${userId}/broadcast`) {
      setActiveMenu("İÇERİK OLUŞTUR");
      setContentMenuOpen(true);
    } else if (location.pathname === `/${userId}/force-send`) {
      setActiveMenu("İÇERİK OLUŞTUR");
      setContentMenuOpen(true);
    } else if (location.pathname === `/${userId}/learners`)
      setActiveMenu("KATILIMCILAR");
    else if (location.pathname === `/${userId}/insights`)
      setActiveMenu("RAPORLAR");
    else if (location.pathname === `/${userId}/archive`)
      setActiveMenu("ARŞİVLER");
    else if (location.pathname === `/${userId}/settings`)
      setActiveMenu("AYARLAR");
    else setActiveMenu("ANASAYFA");
  }, [location, userId]);

  const handleMenuClick = (menuName) => {
    if (menuName === "İÇERİK OLUŞTUR") {
      setContentMenuOpen((prev) => !prev);
      setActiveMenu(menuName);
      return;
    }
    setActiveMenu(menuName);
    setContentMenuOpen(false);
    if (menuName === "ANASAYFA") navigate(`/${userId}/dashboard`);
    else if (menuName === "KURS OLUŞTUR") {
      setActiveMenu("İÇERİK OLUŞTUR");
      navigate(`/${userId}/create-course`);
    } else if (menuName === "ANLIK MESAJ") {
      setActiveMenu("İÇERİK OLUŞTUR");
      navigate(`/${userId}/broadcast`);
    } else if (menuName === "ANLIK İÇERİK") {
      setActiveMenu("İÇERİK OLUŞTUR");
      navigate(`/${userId}/force-send`);
    } else if (menuName === "KATILIMCILAR") navigate(`/${userId}/learners`);
    else if (menuName === "RAPORLAR") navigate(`/${userId}/insights`);
    else if (menuName === "ARŞİVLER") navigate(`/${userId}/archive`);
    else if (menuName === "AYARLAR") navigate(`/${userId}/settings`);
    else if (menuName === "ÇIKIŞ YAP") handleLogout();
  };

  const menuItems = [
    { name: "ANASAYFA", icon: <FiHome className="mr-3" /> },
    {
      name: "İÇERİK OLUŞTUR",
      icon: <FiPlusSquare className="mr-3" />,
      hasSubmenu: true,
      submenu: [
        { name: "KURS OLUŞTUR", icon: <FiBook className="mr-3" /> },
        { name: "ANLIK MESAJ", icon: <FiSend className="mr-3" /> },
        { name: "ANLIK İÇERİK", icon: <FiZap className="mr-3" /> },
      ],
    },
    { name: "KATILIMCILAR", icon: <FiUsers className="mr-3" /> },
    { name: "RAPORLAR", icon: <FiBarChart2 className="mr-3" /> },
    { name: "ARŞİVLER", icon: <FiArchive className="mr-3" /> },
  ];

  const itemWrapper =
    "flex items-center p-3 w-full text-left rounded-lg transition-colors";
  const textBaseInactive = "font-medium text-white/70 hover:text-white";
  const textActive = "font-extrabold text-white";
  const iconClass = (isActive) =>
    `${isActive ? "text-white" : "text-white/70 group-hover:text-white"} mr-3`;

  const handleLogout = async () => {
    try {
      await apiClient.post("/admin/logout");
    } catch (err) {
      console.warn("Logout request failed:", err.message);
    }

    // Token ve user bilgilerini temizle
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    sessionStorage.clear();

    // Login sayfasına yönlendir
    window.location.href = "/";
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div
        className={`bg-gradient-to-b from-[#07183c] to-[#030b1f] text-white shadow-md transition-all duration-300 flex flex-col rounded-xl
                    ${sidebarCollapsed ? "w-20" : "w-64"} m-2 sm:m-5`}
      >
        {/* Logo */}
        <div
          className={`flex items-start justify-start ${
            sidebarCollapsed ? "p-4 h-12" : "pt-8 pr-4 pl-8 h-28"
          }`}
        >
          <img
            src={logoFull}
            alt="ZENOLearn"
            className={`h-20 object-contain ${
              sidebarCollapsed ? "hidden" : "block"
            }`}
            loading="eager"
            decoding="async"
          />
          <img
            src={logoMark}
            alt="ZENO"
            className={`h-8 w-8 object-contain ${
              sidebarCollapsed ? "block" : "hidden"
            }`}
            loading="eager"
            decoding="async"
          />
        </div>

        {/* Menu */}
        <div className="flex-1 overflow-y-auto">
          <nav className="mt-4">
            <div className="px-4">
              <ul>
                {menuItems.map((item) => {
                  const isActive = activeMenu === item.name;
                  const isContentItem = item.hasSubmenu;

                  return (
                    <li key={item.name} className="mb-1">
                      <button
                        onClick={() => handleMenuClick(item.name)}
                        className={`group ${itemWrapper} ${
                          isContentItem ? "justify-between" : ""
                        }`}
                        aria-label={item.name}
                      >
                        <div className="flex items-center">
                          {!sidebarCollapsed &&
                            React.cloneElement(item.icon, {
                              className: iconClass(isActive),
                            })}
                          {!sidebarCollapsed && (
                            <span
                              className={`${
                                isActive ? textActive : textBaseInactive
                              }`}
                            >
                              {item.name}
                            </span>
                          )}
                          {sidebarCollapsed &&
                            React.cloneElement(item.icon, {
                              className: iconClass(isActive).replace(
                                " mr-3",
                                "",
                              ),
                            })}
                        </div>
                        {/* Chevron for submenu */}
                        {isContentItem && !sidebarCollapsed && (
                          <FiChevronDown
                            className={`text-white/70 transition-transform duration-200 ${
                              contentMenuOpen ? "rotate-180" : ""
                            }`}
                          />
                        )}
                      </button>

                      {/* Submenu */}
                      {isContentItem &&
                        contentMenuOpen &&
                        !sidebarCollapsed && (
                          <ul className="ml-6 mt-1 space-y-1">
                            {item.submenu.map((subItem) => {
                              const isSubActive =
                                (subItem.name === "KURS OLUŞTUR" &&
                                  location.pathname ===
                                    `/${userId}/create-course`) ||
                                (subItem.name === "ANLIK MESAJ" &&
                                  location.pathname === `/${userId}/broadcast`);
                              return (
                                <li key={subItem.name}>
                                  <button
                                    onClick={() =>
                                      handleMenuClick(subItem.name)
                                    }
                                    className={`group ${itemWrapper} py-2`}
                                    aria-label={subItem.name}
                                  >
                                    {React.cloneElement(subItem.icon, {
                                      className: iconClass(isSubActive),
                                      size: 16,
                                    })}
                                    <span
                                      className={`text-sm ${
                                        isSubActive
                                          ? textActive
                                          : textBaseInactive
                                      }`}
                                    >
                                      {subItem.name}
                                    </span>
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </nav>
        </div>

        {/* Footer menu */}
        <div className="p-4 border-t border-white/10">
          <ul>
            {["AYARLAR", "ÇIKIŞ YAP"].map((item) => {
              const isActive = activeMenu === item;
              return (
                <li key={item} className="mb-1">
                  <button
                    onClick={() => handleMenuClick(item)}
                    className={`group ${itemWrapper}`}
                    aria-label={item}
                  >
                    {!sidebarCollapsed &&
                      (item === "AYARLAR" ? (
                        <FiSettings className={iconClass(isActive)} />
                      ) : (
                        <FiLogOut className={iconClass(isActive)} />
                      ))}
                    {!sidebarCollapsed && (
                      <span
                        className={`${
                          isActive ? textActive : textBaseInactive
                        }`}
                      >
                        {item}
                      </span>
                    )}
                    {sidebarCollapsed &&
                      (item === "AYARLAR" ? (
                        <FiSettings
                          className={iconClass(isActive).replace(" mr-3", "")}
                        />
                      ) : (
                        <FiLogOut
                          className={iconClass(isActive).replace(" mr-3", "")}
                        />
                      ))}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 overflow-auto">
        <div className="p-4 sm:p-6">
          <div className="flex items-center mb-8">
            <button
              onClick={toggleSidebar}
              className="mr-4 text-gray-600 hover:text-gray-900"
              aria-label={
                sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
              }
              title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <FiMenu className="text-2xl" />
            </button>
            <h1 className="text-2xl font-bold main-header-font">{pageTitle}</h1>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

export default SharedLayout;
